import { action, computed, observable } from 'mobx';
import { sleep, type User, type UserCredential } from '@linktivity/link-utils';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  MAX_REFRESH_COUNT,
  TIMEOUT
} from '@link/share/constants';

const storage = localStorage;

export interface Claims {
  role: string;
  supplier: string;
}

export default class Auth {
  @observable accessor user: User | null = null;
  @observable accessor claims: Claims | null = null;
  @observable accessor accessToken = storage.getItem(ACCESS_TOKEN) || '';
  @observable accessor refreshToken = storage.getItem(REFRESH_TOKEN) || '';

  @action
  setUser(user: User) {
    this.user = user;
    this.getClaims(user);
  }

  @action
  clearUser() {
    this.user = null;
    this.clearTokens();
  }

  @action
  async getClaims(user: User) {
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult) {
      this.setClaims(tokenResult.claims as unknown as Claims);
    }
  }

  @action
  setClaims(claims: Claims) {
    this.claims = claims;
  }

  @action
  setToken(token: string) {
    try {
      storage.setItem(ACCESS_TOKEN, token);
    } finally {
      this.accessToken = token;
    }
  }

  @action
  clearTokens() {
    try {
      storage.removeItem(ACCESS_TOKEN);
      storage.removeItem(REFRESH_TOKEN);
    } finally {
      this.accessToken = '';
      this.refreshToken = '';
    }
  }

  @action
  async getToken() {
    if (this.user) {
      const token = await this.user.getIdToken();
      this.setToken(token);
      return token;
    } else {
      return '';
    }
  }

  @action
  getRefreshedToken = async () => {
    let count = 0;
    const requestToken: () => Promise<string> = async () => {
      const token = await this.getToken();
      if (token) {
        return token;
      } else {
        if (count < MAX_REFRESH_COUNT) {
          count++;
          await sleep(TIMEOUT);
          return await requestToken();
        } else {
          return '';
        }
      }
    };
    return await requestToken();
  };

  @action
  updateToken(credential: UserCredential) {
    credential.user.getIdToken().then(token => {
      this.setToken(token);
    });
  }

  @computed
  get loggedIn(): boolean {
    return !!this.accessToken;
  }

  @computed
  get isPasswordAuth(): boolean {
    return this.user?.providerData.some(
      provider => provider.providerId === 'password'
    )
      ? true
      : false;
  }
}
